import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Anchor, Globe2, BarChart3 } from "lucide-react";
import SourcingMap from "@/components/SourcingMap";
import { COUNTRIES, COAL_VARIETIES, INDUSTRIES } from "@/lib/coal-data";

export default function Home() {
    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative min-h-[100vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/coal-hero.jpg"
                        alt="Pile of premium coal"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-coal-900 via-coal-900/85 to-coal-900/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-coal-900 via-transparent to-coal-900/40" />
                </div>

                <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-36 lg:pb-24 pt-32 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 fade-up">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="w-12 h-px bg-ember" />
                            <span className="text-[11px] tracking-[0.3em] uppercase text-ember font-bold">
                                Est. India · Global Trade Desk
                            </span>
                        </div>
                        <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-8xl tracking-tighter uppercase leading-[0.92] text-white">
                            Fueling India's
                            <br />
                            <span className="text-ember">Heavy Industry.</span>
                            <br />
                            Shipment by Shipment.
                        </h1>
                        <p className="mt-8 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
                            CARBEX INTERNATIONAL is a trusted importer and
                            supplier of premium coal — sourced from Indonesia,
                            South Africa, Australia, USA and Russia, and
                            delivered to power, steel, cement and chemical
                            plants across India.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-enquire"
                                className="group bg-ember hover:bg-ember-hover text-white px-8 py-4 font-bold tracking-[0.18em] uppercase text-sm transition-all flex items-center gap-3"
                            >
                                Request a Quote
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/products"
                                data-testid="hero-cta-products"
                                className="bg-transparent border border-white/20 hover:border-white/60 text-white px-8 py-4 font-bold tracking-[0.18em] uppercase text-sm transition-all hover:bg-white/5"
                            >
                                Explore Coal Varieties
                            </Link>
                        </div>
                    </div>

                    {/* Quick spec card */}
                    <div className="lg:col-span-4 lg:self-end">
                        <div className="bg-coal-900/70 backdrop-blur-md border border-white/10 p-6 lg:p-8">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold mb-4">
                                Live Trade Desk
                            </div>
                            <div className="space-y-4 font-mono text-sm">
                                <RowSpec label="Indonesian GAR 4200" value="● Available" />
                                <RowSpec label="South African RB2" value="● Available" />
                                <RowSpec label="USA Petcoke 7% S" value="● Available" />
                                <RowSpec label="Australian Coking" value="○ On Order" />
                                <RowSpec label="Russian Anthracite" value="● Available" />
                            </div>
                            <div className="mt-6 pt-6 border-t border-white/10">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                                    Daily Quote
                                </div>
                                <a
                                    href="tel:9872227812"
                                    className="font-mono text-base text-white hover:text-ember"
                                >
                                    +91 98722 27812
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <section className="bg-coal-800 border-y border-white/10 overflow-hidden py-6">
                <div className="flex whitespace-nowrap animate-marquee">
                    {[...Array(2)].flatMap((_, i) =>
                        ["INDONESIA", "SOUTH AFRICA", "AUSTRALIA", "USA", "RUSSIA"].map(
                            (c) => (
                                <div
                                    key={`${c}-${i}`}
                                    className="flex items-center gap-6 px-12 text-ember"
                                >
                                    <span className="font-display text-3xl font-bold tracking-tighter">
                                        {c}
                                    </span>
                                    <span className="w-2 h-2 bg-ember rounded-full" />
                                </div>
                            )
                        )
                    )}
                </div>
            </section>

            {/* SOURCING MAP */}
            <section className="section bg-coal-800 border-y border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                            Global Reach · Indian Spine
                        </div>
                        <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight leading-[1] text-white">
                            Five origins.
                            <br />
                            One trusted desk.
                        </h2>
                        <p className="mt-6 text-zinc-400 leading-relaxed">
                            Long-term offtake relationships with mines and
                            export terminals across five continents allow us to
                            move tonnage at the right grade, the right time,
                            and the right CIF cost — every single shipment.
                        </p>
                        <div className="mt-8 space-y-3">
                            {COUNTRIES.map((c) => (
                                <div
                                    key={c.code}
                                    className="flex items-center justify-between border-b border-white/5 pb-3"
                                >
                                    <div>
                                        <div className="text-white font-medium">
                                            {c.name}
                                        </div>
                                        <div className="text-xs text-zinc-500 font-mono">
                                            {c.ports}
                                        </div>
                                    </div>
                                    <div className="text-xs tracking-widest uppercase text-ember">
                                        {c.role}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-8 bg-coal-900 border border-white/10 p-4 lg:p-8">
                        <SourcingMap />
                    </div>
                </div>
            </section>

            {/* FEATURED COAL */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div>
                            <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-3">
                                Coal Varieties
                            </div>
                            <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight text-white">
                                Every grade.
                                <br />
                                Globally sourced.
                            </h2>
                        </div>
                        <Link
                            to="/products"
                            className="text-ember hover:text-white text-sm uppercase tracking-[0.2em] font-bold flex items-center gap-2"
                        >
                            See all 12 varieties{" "}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                        {COAL_VARIETIES.slice(0, 6).map((c) => (
                            <Link
                                to="/products"
                                key={c.slug}
                                data-testid={`featured-coal-${c.slug}`}
                                className="group bg-coal-800 p-8 hover:bg-coal-700 transition-colors flex flex-col"
                            >
                                <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold mb-4">
                                    {c.tag}
                                </div>
                                <h3 className="font-display text-2xl font-semibold text-white mb-3 leading-tight">
                                    {c.name}
                                </h3>
                                <p className="text-zinc-400 text-sm leading-relaxed mb-6 flex-1">
                                    {c.description.split(".")[0]}.
                                </p>
                                <div className="grid grid-cols-2 gap-3 text-xs font-mono border-t border-white/5 pt-4">
                                    <div>
                                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">
                                            GCV
                                        </div>
                                        <div className="text-white">{c.gcv}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">
                                            Ash
                                        </div>
                                        <div className="text-white">{c.ash}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="section bg-coal-800 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                        Why Carbex
                    </div>
                    <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight text-white max-w-3xl">
                        A trading partner you can trust with a ten-rake order.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                        {[
                            {
                                icon: ShieldCheck,
                                title: "Quality Assured",
                                body: "Independent SGS / CCIC certification at load port and discharge. No surprises in the lab report.",
                            },
                            {
                                icon: Anchor,
                                title: "Logistics Backbone",
                                body: "Dedicated chartering desk, panamax / supramax handling at all major Indian east & west coast ports.",
                            },
                            {
                                icon: Globe2,
                                title: "5-Country Origin",
                                body: "Indonesia, South Africa, Australia, USA, Russia — diversified to insulate you from price shocks.",
                            },
                            {
                                icon: BarChart3,
                                title: "Transparent Pricing",
                                body: "Index-linked or fixed-price contracts, clear FOB/CIF terms. Quote inside 24 hours.",
                            },
                        ].map(({ icon: Icon, title, body }) => (
                            <div
                                key={title}
                                className="bg-coal-900 p-8 hover:bg-coal-700 transition-colors"
                            >
                                <Icon className="w-8 h-8 text-ember mb-6" />
                                <h4 className="font-display text-xl font-semibold text-white mb-3">
                                    {title}
                                </h4>
                                <p className="text-zinc-400 text-sm leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES SNAPSHOT */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div>
                            <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-3">
                                Industries Served
                            </div>
                            <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight text-white">
                                Wherever heat
                                <br />
                                meets metal.
                            </h2>
                        </div>
                        <Link
                            to="/industries"
                            className="text-ember hover:text-white text-sm uppercase tracking-[0.2em] font-bold flex items-center gap-2"
                        >
                            See all industries <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                        {INDUSTRIES.slice(0, 6).map((ind, i) => (
                            <div
                                key={ind.name}
                                className="bg-coal-800 p-8 group hover:bg-coal-700 transition-colors"
                            >
                                <div className="flex items-baseline justify-between mb-4">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold">
                                        {ind.kicker}
                                    </div>
                                    <div className="font-mono text-zinc-600 text-xs">
                                        0{i + 1}
                                    </div>
                                </div>
                                <h3 className="font-display text-2xl font-semibold text-white mb-3">
                                    {ind.name}
                                </h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">
                                    {ind.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="section bg-coal-800 border-t border-white/10 relative overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                            Ready to ship
                        </div>
                        <h2 className="font-display font-bold text-4xl sm:text-6xl uppercase tracking-tight text-white leading-[1]">
                            Tell us your spec.
                            <br />
                            We'll quote in 24 hours.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-4">
                        <Link
                            to="/contact"
                            data-testid="closing-cta-enquire"
                            className="bg-ember hover:bg-ember-hover text-white px-8 py-5 font-bold tracking-[0.18em] uppercase text-sm transition-colors text-center"
                        >
                            Send Enquiry
                        </Link>
                        <a
                            href="mailto:carbexinternational@gmail.com"
                            className="bg-transparent border border-white/20 hover:border-white text-white px-8 py-5 font-bold tracking-[0.18em] uppercase text-sm transition-colors text-center"
                        >
                            Email Trade Desk
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}

function RowSpec({ label, value }) {
    const ok = value.startsWith("●");
    return (
        <div className="flex items-center justify-between border-b border-white/5 pb-2">
            <span className="text-zinc-300">{label}</span>
            <span className={ok ? "text-emerald-400" : "text-amber-400"}>
                {value}
            </span>
        </div>
    );
}
